import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import { replyToAddressesApi } from '@/api/replyToAddresses'
import { invalidateReplyToAddressesLookup } from '@/hooks/useReplyToAddressesLookup'
import type { ReplyToAddressDto } from '@/types/replyToAddresses'
import { lowerSafe } from '@/utils/strings'

/** Result of a mutation. `error` holds a user-facing message for failures the caller should toast.
 *  `handled` is true when the failure was a server validation (422) the global error handler already
 *  toasted — the caller must NOT toast again (and there is no raw message to show). */
export interface MutationResult {
  ok: boolean
  error?: string
  handled?: boolean
}

/**
 * Loads + maintains the company's reply-to address book. The API is a REPLACE-WHOLE-LIST PUT, so each
 * mutation (add / remove / set-default) computes the new full list locally — enforcing exactly-one-default
 * — PUTs it, and reflects the persisted response. The server is authoritative and rejects broken
 * invariants with a 422 message, which is surfaced back to the caller.
 */
export function useReplyToAddresses(clientCompanyId: string) {
  const [addresses, setAddresses] = useState<ReplyToAddressDto[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Generation token: only the most recent load applies its result, so a superseded
  // (clientCompanyId changed) or unmounted load can't write stale data.
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!clientCompanyId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await replyToAddressesApi.getReplyToAddresses(clientCompanyId)
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) setAddresses(res.data)
    setLoading(false)
  }, [clientCompanyId])

  useEffect(() => {
    void load()
    // Invalidate any in-flight load on unmount / clientCompanyId change.
    return () => { requestIdRef.current++ }
  }, [load])

  // PUT the whole list, reflect the persisted response, and surface any API (422) error to the caller.
  const persist = useCallback(async (next: ReplyToAddressDto[]): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    setSaving(true)
    setError(null)
    const res = await replyToAddressesApi.updateReplyToAddresses(clientCompanyId, { ReplyToAddresses: next })
    setSaving(false)
    if (res.error || !res.data) {
      // 422 validation errors are already surfaced as readable toasts by the global handler in
      // api/client.ts — don't double-toast or echo the raw JSON. Other failures surface normally.
      if (res.status === 422) return { ok: false, handled: true }
      const message = res.error || 'Could not save reply-to addresses.'
      setError(message)
      return { ok: false, error: message }
    }
    setAddresses(res.data)
    // The activation editor reads a module-cached lookup of these addresses — invalidate it so the
    // next editor open reflects this change instead of a stale option set.
    invalidateReplyToAddressesLookup()
    return { ok: true }
  }, [clientCompanyId])

  // Add a new address. The first address added to an empty book becomes the default; an explicit
  // makeDefault flips every other entry off so exactly one stays default.
  const addAddress = useCallback((email: string, makeDefault = false): Promise<MutationResult> => {
    const trimmed = email.trim()
    const current = addresses ?? []
    if (current.some(a => lowerSafe(a.Email) === trimmed.toLowerCase())) {
      return Promise.resolve({ ok: false, error: 'That address is already in the book.' })
    }
    const shouldDefault = makeDefault || current.length === 0
    const next: ReplyToAddressDto[] = [
      ...current.map(a => (shouldDefault ? { ...a, IsDefault: false } : a)),
      { Email: trimmed, IsDefault: shouldDefault },
    ]
    return persist(next)
  }, [addresses, persist])

  // Remove an address. If the removed one was the default and others remain, promote the first
  // survivor so the book keeps exactly one default.
  const removeAddress = useCallback((email: string): Promise<MutationResult> => {
    const current = addresses ?? []
    const remaining = current.filter(a => lowerSafe(a.Email) !== email.toLowerCase())
    const next = remaining.length > 0 && !remaining.some(a => a.IsDefault)
      ? remaining.map((a, i) => ({ ...a, IsDefault: i === 0 }))
      : remaining
    return persist(next)
  }, [addresses, persist])

  // Flip IsDefault so exactly one address (the chosen one) is the default, then PUT.
  const setDefault = useCallback((email: string): Promise<MutationResult> => {
    const current = addresses ?? []
    const next = current.map(a => ({ ...a, IsDefault: lowerSafe(a.Email) === email.toLowerCase() }))
    return persist(next)
  }, [addresses, persist])

  return { addresses, loading, saving, error, load, addAddress, removeAddress, setDefault }
}
